import { useReducer, useEffect, useRef, useState, type ReactNode } from "react";
import { useNavigate, useParams } from "@tanstack/react-router";
import type { GetPickingResponse, ScanRequest, ScannedBagInput } from "@pm-barcode/shared";
import { useApiClient } from "../../hooks/use-api-client.ts";
import { useScanner } from "../../hooks/use-scanner.ts";
import { bagTotal, useLeaveGuard } from "../../hooks/use-leave-guard.ts";
import type { ManualScanResponse } from "@pm-barcode/shared";
import { RequestError } from "../../api/client.ts";
import { ManualBagForm } from "../../components/manual-bag-form.tsx";
import { BagCountPrompt } from "../../components/bag-count-prompt.tsx";

/** A scanned non-serial bag awaiting a bag-count entry. */
interface PendingCount {
  productId: number;
  productName: string;
  lotId: number;
  lotName: string;
  moveId: number;
  sourceLocationId: number;
  destLocationId: number;
  tracking: string;
  batch: string;
  bagNumber?: string;
  weightKg: number;
}
import { useSettings } from "../../context/settings-context.tsx";
import { pickingStateLabel } from "../../lib/picking-state.ts";
import { batchOfLotName } from "../../lib/batch.ts";
import { allocateDispatch, batchTotals, productTotals } from "../../lib/dispatch-lines.ts";

// ─── Audio / Haptic ─────────────────────────────────────────────────────────

function vibrate(pattern: number | number[]): void {
  if (!("vibrate" in navigator)) return;
  navigator.vibrate(pattern);
}

let audioCtx: AudioContext | null = null;

function beep(type: "success" | "error"): void {
  try {
    if (!audioCtx) audioCtx = new AudioContext();
    const play = () => {
      const osc = audioCtx!.createOscillator();
      const gain = audioCtx!.createGain();
      osc.connect(gain);
      gain.connect(audioCtx!.destination);
      osc.frequency.value = type === "success" ? 880 : 220;
      const now = audioCtx!.currentTime;
      gain.gain.setValueAtTime(0.3, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.15);
      osc.start(now);
      osc.stop(now + 0.15);
    };
    // WebView AudioContext may start suspended when triggered from native code.
    if (audioCtx.state === "suspended") {
      void audioCtx.resume().then(play);
    } else {
      play();
    }
  } catch {
    // Ignore if audio is unavailable
  }
}

// ─── State machine ──────────────────────────────────────────────────────────

type Feedback =
  | { kind: "ok"; productName: string; weightKg: number; batch: string; bagNumber?: string }
  | { kind: "err"; title: string };

type Modal =
  | { kind: "unknown"; rawBarcode: string }
  | { kind: "not_on_picking"; category: string; batch: string; suggestion?: { productId: number; productName: string } }
  | { kind: "already_scanned"; lotName: string }
  | { kind: "already_received"; lotName: string }
  | { kind: "variance"; expected: number; actual: number }
  | { kind: "quarantined"; message: string }
  | { kind: "not_reserved"; message: string }
  | { kind: "already_picked"; message: string }
  | { kind: "batch_full"; batch: string; reserved: number }
  | { kind: "error"; title: string; message: string };

interface State {
  phase: "loading" | "load_error" | "ready" | "validating" | "done";
  picking: GetPickingResponse | null;
  errorMsg: string | null;
  /**
   * Quantity scanned this session, by delivery line (move id) — not by product, which counted a
   * bag once per line when a product had two (see lib/dispatch-lines.ts).
   */
  localQty: Record<number, number>;
  /** Bags scanned this session — passed to the validate endpoint for deferred commit. */
  scannedBags: ScannedBagInput[];
  feedback: Feedback | null;
  modal: Modal | null;
  scanSeq: number;
  backorder: { created: boolean; ref?: string } | null;
}

type Action =
  | { type: "LOADED"; picking: GetPickingResponse }
  | { type: "LOAD_FAIL"; msg: string }
  | { type: "SCAN_OK"; productName: string; weightKg: number; batch: string; bagNumber?: string; bags: ScannedBagInput[] }
  | { type: "SCAN_UNKNOWN"; rawBarcode: string }
  | { type: "SCAN_NOT_ON_PICKING"; category: string; batch: string; suggestion?: { productId: number; productName: string } }
  | { type: "SCAN_ALREADY_SCANNED"; lotName: string }
  | { type: "SCAN_ALREADY_RECEIVED"; lotName: string }
  | { type: "SCAN_QUARANTINED"; message: string }
  | { type: "SCAN_NOT_RESERVED"; message: string }
  | { type: "SCAN_ALREADY_PICKED"; message: string }
  | { type: "SCAN_BATCH_FULL"; batch: string; reserved: number }
  | { type: "SCAN_VARIANCE"; expected: number; actual: number; bags: ScannedBagInput[] }
  | { type: "SCAN_ERR"; title: string; msg: string }
  | { type: "DISMISS" }
  | { type: "VALIDATE_START" }
  | { type: "VALIDATE_OK"; backorderCreated: boolean; backorderRef?: string }
  | { type: "VALIDATE_FAIL"; msg: string };

const initialState: State = {
  phase: "loading",
  picking: null,
  errorMsg: null,
  localQty: {},
  scannedBags: [],
  feedback: null,
  modal: null,
  scanSeq: 0,
  backorder: null,
};

/** Add scanned bags to the per-line session counts. */
function addToLines(localQty: Record<number, number>, bags: readonly ScannedBagInput[]): Record<number, number> {
  const next = { ...localQty };
  for (const bag of bags) {
    next[bag.moveId] = (next[bag.moveId] ?? 0) + bag.quantity;
  }
  return next;
}

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case "LOADED":
      return { ...state, phase: "ready", picking: action.picking };
    case "LOAD_FAIL":
      return { ...state, phase: "load_error", errorMsg: action.msg };
    case "SCAN_OK":
      return {
        ...state,
        scanSeq: state.scanSeq + 1,
        feedback: {
          kind: "ok",
          productName: action.productName,
          weightKg: action.weightKg,
          batch: action.batch,
          bagNumber: action.bagNumber,
        },
        localQty: addToLines(state.localQty, action.bags),
        scannedBags: [...state.scannedBags, ...action.bags],
        modal: null,
      };
    case "SCAN_VARIANCE":
      // Over-dispatch bag: warn but still track for commit at validate time.
      return {
        ...state,
        scanSeq: state.scanSeq + 1,
        feedback: { kind: "err", title: "Extra bag" },
        localQty: addToLines(state.localQty, action.bags),
        scannedBags: [...state.scannedBags, ...action.bags],
        modal: { kind: "variance", expected: action.expected, actual: action.actual },
      };
    case "SCAN_UNKNOWN":
      return {
        ...state,
        scanSeq: state.scanSeq + 1,
        feedback: { kind: "err", title: "Unknown barcode" },
        modal: { kind: "unknown", rawBarcode: action.rawBarcode },
      };
    case "SCAN_NOT_ON_PICKING":
      return {
        ...state,
        scanSeq: state.scanSeq + 1,
        feedback: { kind: "err", title: "Not on picking" },
        modal: { kind: "not_on_picking", category: action.category, batch: action.batch, suggestion: action.suggestion },
      };
    case "SCAN_ALREADY_SCANNED":
      return {
        ...state,
        scanSeq: state.scanSeq + 1,
        feedback: { kind: "err", title: "Already scanned" },
        modal: { kind: "already_scanned", lotName: action.lotName },
      };
    case "SCAN_ALREADY_RECEIVED":
      return {
        ...state,
        scanSeq: state.scanSeq + 1,
        feedback: { kind: "err", title: "Already dispatched" },
        modal: { kind: "already_received", lotName: action.lotName },
      };
    case "SCAN_QUARANTINED":
      return {
        ...state,
        scanSeq: state.scanSeq + 1,
        feedback: { kind: "err", title: "Quarantined — do not pick" },
        modal: { kind: "quarantined", message: action.message },
      };
    case "SCAN_NOT_RESERVED":
      return {
        ...state,
        scanSeq: state.scanSeq + 1,
        feedback: { kind: "err", title: "Wrong bag — not reserved" },
        modal: { kind: "not_reserved", message: action.message },
      };
    case "SCAN_ALREADY_PICKED":
      return {
        ...state,
        scanSeq: state.scanSeq + 1,
        feedback: { kind: "err", title: "Already picked" },
        modal: { kind: "already_picked", message: action.message },
      };
    case "SCAN_BATCH_FULL":
      return {
        ...state,
        scanSeq: state.scanSeq + 1,
        feedback: { kind: "err", title: "Batch complete" },
        modal: { kind: "batch_full", batch: action.batch, reserved: action.reserved },
      };
    case "SCAN_ERR":
      return {
        ...state,
        scanSeq: state.scanSeq + 1,
        feedback: { kind: "err", title: action.title },
        modal: { kind: "error", title: action.title, message: action.msg },
      };
    case "DISMISS":
      return { ...state, modal: null };
    case "VALIDATE_START":
      return { ...state, phase: "validating", errorMsg: null };
    case "VALIDATE_OK":
      return {
        ...state,
        phase: "done",
        backorder: { created: action.backorderCreated, ref: action.backorderRef },
      };
    case "VALIDATE_FAIL":
      return { ...state, phase: "ready", errorMsg: action.msg };
    default:
      return state;
  }
}

// ─── Component ───────────────────────────────────────────────────────────────

export function DispatchPicking() {
  const navigate = useNavigate();
  const api = useApiClient();
  const { hapticEnabled, setHapticEnabled } = useSettings();
  const [state, dispatch] = useReducer(reducer, initialState);
  const scanInFlight = useRef(false);
  // Ref-based dedup: updated synchronously on accept, no closure-timing gap.
  const scannedSerialIdsRef = useRef<Set<number>>(new Set());
  const [showStaleWarning, setShowStaleWarning] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [pendingCount, setPendingCount] = useState<PendingCount | null>(null);
  const loadedAtRef = useRef<number | null>(null);

  /**
   * Put a scanned bag (or counted lot entry) on the right delivery line(s). The Worker names the
   * product's first line; with two lines for one product that sent every bag to the first, so
   * the screen allocates by the batch's reservation and this session's own per-line counts.
   */
  function toLineBags(bag: ScannedBagInput): ScannedBagInput[] {
    const shares = allocateDispatch(state.picking?.moves ?? [], state.scannedBags, bag.productId, bag.batch, bag.quantity);
    return shares.length === 0
      ? [bag]
      : shares.map((share) => ({ ...bag, moveId: share.moveId, quantity: share.quantity }));
  }

  // Confirm the bag count for a scanned non-serial (lot) product. Re-checks the per-batch cap
  // (summing quantities) since the count could exceed what Odoo reserved for the batch.
  function confirmCount(count: number): void {
    const p = pendingCount;
    if (!p) return;
    const bag: ScannedBagInput = {
      lotId: p.lotId,
      lotName: p.lotName,
      batch: p.batch,
      quantity: count,
      moveId: p.moveId,
      sourceLocationId: p.sourceLocationId,
      destLocationId: p.destLocationId,
      productId: p.productId,
      tracking: p.tracking,
    };
    const moves = state.picking?.moves ?? [];
    const { reserved: reservedForBatch, scanned: scannedForBatch } = batchTotals(
      moves,
      state.scannedBags,
      p.productId,
      p.batch,
    );
    if (reservedForBatch > 0 && scannedForBatch + count > reservedForBatch) {
      beep("error");
      if (hapticEnabled) vibrate([100, 50, 100]);
      dispatch({ type: "SCAN_BATCH_FULL", batch: p.batch, reserved: reservedForBatch });
      setPendingCount(null);
      return;
    }
    const bags = toLineBags(bag);
    const totals = productTotals(moves, state.scannedBags, p.productId);
    const newQty = totals.picked + count;
    if (hapticEnabled) vibrate(200);
    if (totals.expected > 0 && newQty > totals.expected) {
      dispatch({ type: "SCAN_VARIANCE", expected: totals.expected, actual: newQty, bags });
    } else {
      dispatch({
        type: "SCAN_OK",
        productName: p.productName,
        weightKg: p.weightKg * count, // total weight of the counted bags
        batch: p.batch,
        bagNumber: p.bagNumber,
        bags,
      });
    }
    setPendingCount(null);
  }

  // Add a damaged-barcode bag (recorded via /scan/manual) to the session, like a scan. The
  // server guards (reserved batch / Ready / on-hand) already validated it.
  function onManualAdded(result: ManualScanResponse): void {
    const bag: ScannedBagInput = {
      lotId: result.odoo.lotId,
      lotName: result.odoo.lotName,
      batch: result.batch,
      quantity: result.odoo.qtyDone,
      moveId: result.odoo.moveId,
      sourceLocationId: result.odoo.sourceLocationId,
      destLocationId: result.odoo.destLocationId,
      productId: result.odoo.productId,
      tracking: result.odoo.tracking,
    };
    if (result.odoo.tracking === "serial") scannedSerialIdsRef.current.add(result.odoo.lotId);
    beep("success");
    if (hapticEnabled) vibrate(200);
    dispatch({
      type: "SCAN_OK",
      productName: result.odoo.productName,
      weightKg: result.weightKg,
      batch: result.batch,
      bagNumber: result.bagNumber,
      bags: toLineBags(bag),
    });
    setShowManual(false);
  }

  const { ref } = useParams({ strict: false }) as { ref: string };

  useEffect(() => {
    if (!api || !ref) return;
    let cancelled = false;
    api
      .getPicking(ref)
      .then((picking) => {
        if (!cancelled) dispatch({ type: "LOADED", picking });
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          dispatch({
            type: "LOAD_FAIL",
            msg: err instanceof Error ? err.message : "Failed to load picking",
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [api, ref]);

  // Capture load timestamp for stale-data warning.
  useEffect(() => {
    if (state.phase === "ready" && loadedAtRef.current === null) {
      loadedAtRef.current = Date.now();
    }
  }, [state.phase]);

  // Show stale warning after 20 minutes on the same picking.
  useEffect(() => {
    if (state.phase !== "ready") return;
    const interval = setInterval(() => {
      if (loadedAtRef.current && Date.now() - loadedAtRef.current >= 20 * 60 * 1000) {
        setShowStaleWarning(true);
      }
    }, 60_000);
    return () => clearInterval(interval);
  }, [state.phase]);

  // Confirm before leaving (← button, hardware back, reload) while there are unsaved scans.
  const leave = useLeaveGuard(
    (state.phase === "ready" || state.phase === "validating") && state.scannedBags.length > 0,
  );

  useScanner((event) => {
    if (state.phase !== "ready" || !api || scanInFlight.current || pendingCount) return;
    scanInFlight.current = true;

    const body: ScanRequest = {
      rawBarcode: event.rawBarcode,
      symbology: event.symbology,
      workflow: "dispatch",
      pickingRef: ref,
      deviceId: import.meta.env.VITE_DEVICE_ID,
      capturedAt: event.capturedAt,
    };

    void api
      .postScan(body)
      .then((result) => {
        if (result.outcome === "ok") {
          // Non-serial (lot) products: prompt for the number of bags instead of re-scanning.
          // The reserved-batch / quarantine / on-hand guards already ran server-side on the scan.
          if (result.odoo.tracking !== "serial") {
            beep("success");
            if (hapticEnabled) vibrate(120);
            setPendingCount({
              productId: result.odoo.productId,
              productName: result.odoo.productName,
              lotId: result.odoo.lotId,
              lotName: result.odoo.lotName,
              moveId: result.odoo.moveId,
              sourceLocationId: result.odoo.sourceLocationId,
              destLocationId: result.odoo.destLocationId,
              tracking: result.odoo.tracking,
              batch: result.normalised.batch,
              bagNumber: result.normalised.bagNumber,
              weightKg: result.normalised.weightKg,
            });
            return;
          }

          const bag: ScannedBagInput = {
            lotId: result.odoo.lotId,
            lotName: result.odoo.lotName,
            batch: result.normalised.batch,
            quantity: result.odoo.qtyDone, // 1 unit per bag — the kg weight is display-only
            moveId: result.odoo.moveId,
            sourceLocationId: result.odoo.sourceLocationId,
            destLocationId: result.odoo.destLocationId,
            productId: result.odoo.productId,
            tracking: result.odoo.tracking,
          };

          // Duplicate detection for serial products — ref avoids any closure-timing gap.
          if (result.odoo.tracking === "serial" && scannedSerialIdsRef.current.has(result.odoo.lotId)) {
            beep("error");
            if (hapticEnabled) vibrate([100, 50, 100]);
            dispatch({ type: "SCAN_ALREADY_SCANNED", lotName: result.odoo.lotName });
            return;
          }

          const moves = state.picking?.moves ?? [];

          // Per-batch count: any bag of a reserved batch is acceptable, up to the number Odoo
          // reserved for that batch across all the product's lines. Once that batch's count is
          // met, reject (pick another batch) so we don't over-ship one batch and under-ship another.
          const scanBatch = result.normalised.batch;
          const { reserved: reservedForBatch, scanned: scannedForBatch } = batchTotals(
            moves,
            state.scannedBags,
            result.odoo.productId,
            scanBatch,
          );
          if (reservedForBatch > 0 && scannedForBatch >= reservedForBatch) {
            beep("error");
            if (hapticEnabled) vibrate([100, 50, 100]);
            if (result.odoo.tracking === "serial") scannedSerialIdsRef.current.add(result.odoo.lotId);
            dispatch({ type: "SCAN_BATCH_FULL", batch: scanBatch, reserved: reservedForBatch });
            return;
          }

          // Client-side over-dispatch check, against everything expected of this product across
          // all its lines.
          const bags = toLineBags(bag);
          const totals = productTotals(moves, state.scannedBags, result.odoo.productId);
          const newQty = totals.picked + result.odoo.qtyDone;
          if (totals.expected > 0 && newQty > totals.expected) {
            beep("error");
            if (hapticEnabled) vibrate([100, 50, 100]);
            if (result.odoo.tracking === "serial") scannedSerialIdsRef.current.add(result.odoo.lotId);
            dispatch({ type: "SCAN_VARIANCE", expected: totals.expected, actual: newQty, bags });
            return;
          }

          if (result.odoo.tracking === "serial") scannedSerialIdsRef.current.add(result.odoo.lotId);
          const newLocalQty = addToLines(state.localQty, bags);
          const allComplete = state.picking?.moves.every(
            (m) => m.doneQty + (newLocalQty[m.id] ?? 0) >= m.expectedQty,
          ) ?? false;
          beep("success");
          if (hapticEnabled) vibrate(allComplete ? [100, 50, 100, 50, 200] : 200);
          dispatch({
            type: "SCAN_OK",
            productName: result.odoo.productName,
            weightKg: result.normalised.weightKg,
            batch: result.normalised.batch,
            bagNumber: result.normalised.bagNumber,
            bags,
          });
        } else if (result.outcome === "unknown") {
          beep("error");
          if (hapticEnabled) vibrate([100, 50, 100]);
          dispatch({ type: "SCAN_UNKNOWN", rawBarcode: result.rawBarcode });
        } else if (result.outcome === "not_on_picking") {
          beep("error");
          if (hapticEnabled) vibrate([100, 50, 100]);
          dispatch({
            type: "SCAN_NOT_ON_PICKING",
            category: result.normalised.category,
            batch: result.normalised.batch,
            suggestion: result.suggestion,
          });
        }
      })
      .catch((err: unknown) => {
        beep("error");
        if (hapticEnabled) vibrate([100, 50, 100]);
        if (err instanceof RequestError && err.code === "quarantined") {
          dispatch({ type: "SCAN_QUARANTINED", message: err.message });
        } else if (err instanceof RequestError && err.code === "not_reserved") {
          dispatch({ type: "SCAN_NOT_RESERVED", message: err.message });
        } else if (err instanceof RequestError && err.code === "already_picked") {
          dispatch({ type: "SCAN_ALREADY_PICKED", message: err.message });
        } else if (err instanceof RequestError && err.code === "duplicate_serial") {
          dispatch({ type: "SCAN_ALREADY_RECEIVED", lotName: err.message.match(/Bag (.+?) has/)?.[1] ?? "this bag" });
        } else {
          dispatch({
            type: "SCAN_ERR",
            title: err instanceof RequestError && err.code === "not_in_stock" ? "Not in stock" : "Scan failed",
            msg: err instanceof Error ? err.message : "Unknown error",
          });
        }
      })
      .finally(() => {
        scanInFlight.current = false;
      });
  });

  async function handleValidate() {
    if (!api || state.phase !== "ready") return;
    dispatch({ type: "VALIDATE_START" });
    try {
      const result = await api.validatePicking(ref, { acceptBackorder: true, scans: state.scannedBags });
      dispatch({
        type: "VALIDATE_OK",
        backorderCreated: result.backorderCreated,
        backorderRef: result.backorderPickingRef,
      });
    } catch (err) {
      dispatch({
        type: "VALIDATE_FAIL",
        msg: err instanceof Error ? err.message : "Validation failed",
      });
    }
  }



  // ── Loading ──
  if (state.phase === "loading") {
    return (
      <div className="flex flex-1 items-center justify-center">
        <p className="text-gray-400">Loading picking…</p>
      </div>
    );
  }

  // ── Load error ──
  if (state.phase === "load_error") {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-4 p-6 text-center">
        <p className="text-lg font-semibold text-red-400">Cannot open picking</p>
        <p className="text-sm text-gray-400">{state.errorMsg}</p>
        <button
          onClick={() => void navigate({ to: "/dispatch", replace: true })}
          className="rounded-xl bg-gray-800 px-6 py-3 font-semibold active:bg-gray-700"
        >
          Back to picking list
        </button>
      </div>
    );
  }

  // ── Done ──
  if (state.phase === "done") {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-6 p-6 text-center">
        <div className="text-6xl text-green-400">✓</div>
        <div>
          <p className="text-xl font-bold">Dispatch validated</p>
          <p className="mt-1 font-mono text-gray-300">{ref}</p>
        </div>
        {state.backorder?.created && state.backorder.ref && (
          <div className="rounded-xl bg-yellow-900/40 px-4 py-3 text-sm text-yellow-300">
            Backorder created: {state.backorder.ref}
          </div>
        )}
        <button
          onClick={() => void navigate({ to: "/workflow", replace: true })}
          className="w-full max-w-xs rounded-xl bg-green-600 py-4 text-lg font-semibold active:bg-green-700"
        >
          Back to workflow
        </button>
      </div>
    );
  }

  // ── Ready / Validating ──
  const picking = state.picking!;

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-3 border-b border-gray-800 px-4 py-3">
        <button
          onClick={() => void navigate({ to: "/dispatch", replace: true })}
          className="text-sm text-gray-400 active:text-white"
        >
          ←
        </button>
        <div className="min-w-0 flex-1">
          <p className="font-mono text-sm font-semibold">{picking.picking.name}</p>
          <p className="truncate text-xs text-gray-500">
            {pickingStateLabel(picking.picking.state)}
            {picking.picking.partner ? ` · ${picking.picking.partner}` : ""}
          </p>
          {picking.picking.origin && (
            <p className="truncate text-xs text-gray-600">{picking.picking.origin}</p>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => {
              const next = !hapticEnabled;
              setHapticEnabled(next);
              if (next) vibrate(100);
            }}
            className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${hapticEnabled ? "bg-green-600 text-white" : "bg-gray-800 text-gray-500"}`}
          >
            Buzz
          </button>
          <span className="rounded-full bg-green-900/50 px-2 py-0.5 text-xs font-semibold uppercase tracking-wider text-green-300">
            Dispatch
          </span>
        </div>
      </div>

      {/* Stale data warning */}
      {showStaleWarning && (
        <div className="mx-4 mt-2 flex items-center justify-between rounded-xl bg-yellow-900/40 px-3 py-2 text-xs text-yellow-300">
          <span>Picking data may be out of date — reload if in doubt.</span>
          <button onClick={() => setShowStaleWarning(false)} className="ml-3 text-yellow-500 active:text-yellow-200">✕</button>
        </div>
      )}

      {/* Picking error (validate failed) */}
      {state.errorMsg && (
        <div className="mx-4 mt-3 rounded-xl bg-red-900/40 px-4 py-3 text-sm text-red-300">
          <p className="font-semibold">Validation failed — tap Retry to try again.</p>
          <p className="mt-1 text-xs text-red-400">{state.errorMsg}</p>
        </div>
      )}

      {/* Progress bar */}
      {(() => {
        const totalExpected = picking.moves.reduce((s, m) => s + m.expectedQty, 0);
        const totalDone = picking.moves.reduce((s, m) => s + m.doneQty + (state.localQty[m.id] ?? 0), 0);
        const pct = totalExpected > 0 ? Math.min(100, (totalDone / totalExpected) * 100) : 0;
        const complete = totalDone >= totalExpected;
        return (
          <div className="px-4 pb-2 pt-3">
            <div className="mb-1 flex items-center justify-between text-sm">
              <span className="text-gray-500">Progress</span>
              <span className={`font-semibold tabular-nums ${complete ? "text-green-400" : "text-white"}`}>
                {totalDone} / {totalExpected} bags
              </span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-gray-800">
              <div
                className={`h-full rounded-full transition-all duration-300 ${complete ? "bg-green-500" : "bg-green-600"}`}
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        );
      })()}

      {/* Move lines table */}
      <div className="flex-1 overflow-y-auto">
        <table className="w-full">
          <thead className="sticky top-0 bg-gray-900">
            <tr className="border-b border-gray-800 text-xs uppercase tracking-widest text-gray-500">
              <th className="py-2 pl-4 text-left font-normal">Product / Batch</th>
              <th className="py-2 pr-3 text-right font-normal">Plan</th>
              <th className="py-2 pr-4 text-right font-normal">Picked</th>
            </tr>
          </thead>
          <tbody>
            {picking.moves.map((move) => {
              const localQty = state.localQty[move.id] ?? 0;
              const done = move.doneQty + localQty;
              const complete = done >= move.expectedQty;
              // Reserved bags grouped by BATCH (serials within a batch are interchangeable —
              // the operator may scan any bag of a reserved batch). reservedByBatch = how many
              // bags of each batch Odoo wants on this line; scannedByBatch = how many this
              // session has put on this line.
              const reservedByBatch = new Map<string, number>();
              for (const lot of move.reservedLots ?? []) {
                const b = batchOfLotName(lot.lotName);
                reservedByBatch.set(b, (reservedByBatch.get(b) ?? 0) + lot.reservedQty);
              }
              const scannedByBatch = new Map<string, number>();
              for (const bag of state.scannedBags) {
                if (bag.moveId !== move.id) continue;
                scannedByBatch.set(bag.batch, (scannedByBatch.get(bag.batch) ?? 0) + bag.quantity);
              }
              return (
                <>
                  <tr key={move.id} className="border-b border-gray-800/30">
                    <td className="pb-1 pl-4 pt-3 text-sm font-semibold leading-tight">{move.product.name}</td>
                    <td className="pb-1 pr-3 pt-3 text-right tabular-nums text-gray-400">
                      {move.expectedQty}
                    </td>
                    <td className={`pb-1 pr-4 pt-3 text-right tabular-nums ${complete ? "font-semibold text-green-400" : "text-white"}`}>
                      {done}
                      {localQty > 0 && (
                        <span className="ml-1 text-xs font-normal text-blue-400">+{localQty}</span>
                      )}
                    </td>
                  </tr>
                  {reservedByBatch.size > 0 && (
                    <tr key={`${move.id}-lots`} className="border-b border-gray-800/60">
                      <td colSpan={3} className="pb-3 pl-4 pr-4">
                        <div className="flex flex-wrap gap-1.5 pt-0.5">
                          {[...reservedByBatch.entries()].map(([batch, reserved]) => {
                            const scanned = scannedByBatch.get(batch) ?? 0;
                            const batchDone = scanned >= reserved;
                            return (
                              <span
                                key={batch}
                                className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-mono text-xs ${
                                  batchDone
                                    ? "bg-green-900/50 text-green-400"
                                    : "bg-gray-800 text-gray-300"
                                }`}
                              >
                                {batchDone && <span className="not-italic">✓</span>}
                                {batch}
                                <span className="text-gray-500">{scanned}/{reserved}</span>
                              </span>
                            );
                          })}
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Scan feedback panel */}
      <div className="border-t border-gray-800 p-4">
        <div
          key={state.scanSeq}
          className={`rounded-2xl p-4 text-center transition-colors ${
            state.feedback?.kind === "ok"
              ? "bg-scan-ok/20 animate-flash"
              : state.feedback?.kind === "err"
                ? "bg-scan-error/20 animate-flash"
                : "bg-gray-900"
          }`}
        >
          {state.feedback?.kind === "ok" ? (
            <>
              <p className="text-2xl font-bold text-scan-ok">
                ✓ {state.feedback.weightKg} kg
              </p>
              <p className="mt-0.5 text-sm text-gray-300">{state.feedback.productName}</p>
              <p className="text-xs text-gray-500">
                Batch {state.feedback.batch}
                {state.feedback.bagNumber ? ` · Bag ${state.feedback.bagNumber}` : ""}
              </p>
            </>
          ) : state.feedback?.kind === "err" ? (
            <p className="font-semibold text-scan-error">{state.feedback.title}</p>
          ) : (
            <p className="text-sm text-gray-500">Ready to scan</p>
          )}
          {state.scannedBags.length > 0 && (
            <p className="mt-2 text-xs text-gray-600">
              {bagTotal(state.scannedBags)} bag{bagTotal(state.scannedBags) !== 1 ? "s" : ""} scanned this session
            </p>
          )}
        </div>
      </div>

      {/* Validate button */}
      <div className="px-4 pb-6">
        <button
          onClick={() => setShowManual(true)}
          className="mb-2 w-full text-sm text-gray-400 underline active:text-white"
        >
          Damaged barcode? Add bag manually
        </button>
        <button
          onClick={() => void handleValidate()}
          disabled={state.phase === "validating" || state.scannedBags.length === 0}
          className="w-full rounded-xl bg-green-600 py-4 text-lg font-semibold active:bg-green-700 disabled:opacity-50"
        >
          {state.phase === "validating" ? "Validating…" : state.errorMsg ? "Retry Validation" : "Validate Dispatch"}
        </button>
      </div>

      {/* Bag-count prompt for a scanned non-serial (lot) product */}
      {pendingCount && (
        <BagCountPrompt
          productName={pendingCount.productName}
          batch={pendingCount.batch}
          bagNumber={pendingCount.bagNumber}
          onConfirm={confirmCount}
          onCancel={() => setPendingCount(null)}
        />
      )}

      {/* Manual bag entry (damaged barcode) */}
      {showManual && (
        <ManualBagForm
          workflow="dispatch"
          pickingRef={ref}
          products={picking.moves.map((m) => ({ id: m.product.id, name: m.product.name, tracking: m.product.tracking }))}
          defaultBatch={state.scannedBags[state.scannedBags.length - 1]?.batch}
          defaultProductId={state.scannedBags[state.scannedBags.length - 1]?.productId}
          onAdded={onManualAdded}
          onClose={() => setShowManual(false)}
        />
      )}

      {/* Exception modal */}
      {state.modal && <ExceptionModal modal={state.modal} onDismiss={() => dispatch({ type: "DISMISS" })} />}

      {/* Leave confirmation */}
      {leave.status === "blocked" && (
        <div className="fixed inset-0 flex items-end justify-center bg-black/70 p-4">
          <div className="w-full max-w-sm rounded-2xl bg-gray-800 p-6">
            <h3 className="mb-2 text-lg font-bold text-red-400">Leave picking?</h3>
            <p className="mb-5 text-sm text-gray-400">
              You have scanned <span className="font-semibold text-white">{bagTotal(state.scannedBags)} bag{bagTotal(state.scannedBags) !== 1 ? "s" : ""}</span> that have not been saved.
              Leaving now will lose all scans for this session.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => leave.reset()}
                className="flex-1 rounded-xl bg-gray-700 py-3 font-semibold active:bg-gray-600"
              >
                Keep scanning
              </button>
              <button
                onClick={() => leave.proceed()}
                className="flex-1 rounded-xl bg-red-700 py-3 font-semibold active:bg-red-600"
              >
                Leave
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Exception modal ─────────────────────────────────────────────────────────

interface ExceptionModalProps {
  modal: Modal;
  onDismiss: () => void;
}

function ExceptionModal({ modal, onDismiss }: ExceptionModalProps) {
  let title: string;
  let body: ReactNode;

  if (modal.kind === "unknown") {
    title = "Unknown Barcode";
    body = (
      <>
        <p className="text-gray-300">This barcode was not recognised and has been flagged for review.</p>
        <p className="mt-2 break-all font-mono text-xs text-gray-500">{modal.rawBarcode}</p>
        <p className="mt-1 font-mono text-xs text-gray-600">
          {/* eslint-disable-next-line no-control-regex */}
          {modal.rawBarcode.length} chars · {(modal.rawBarcode.match(/\x1d/g) ?? []).length} GS
        </p>
      </>
    );
  } else if (modal.kind === "not_on_picking") {
    title = "Not on Picking";
    body = (
      <>
        <p className="text-gray-300">
          <span className="font-semibold capitalize">{modal.category.replace(/_/g, " ")}</span>{" "}
          batch <span className="font-mono">{modal.batch}</span> is not expected on this picking.
        </p>
        {modal.suggestion && (
          <p className="mt-2 text-sm text-gray-400">
            Closest match: <span className="font-semibold text-gray-200">{modal.suggestion.productName}</span>. Ask your supervisor to update the delivery order if this is correct.
          </p>
        )}
      </>
    );
  } else if (modal.kind === "already_scanned") {
    title = "Already Scanned";
    body = (
      <p className="text-gray-300">
        Bag <span className="font-mono font-semibold">{modal.lotName}</span> has already been
        scanned on this picking. Skip it and move to the next bag.
      </p>
    );
  } else if (modal.kind === "already_received") {
    title = "Already Dispatched";
    body = (
      <p className="text-gray-300">
        Bag <span className="font-mono font-semibold">{modal.lotName}</span> has already been
        dispatched from stock. Each bag can only be scanned once — check the label and try a
        different bag.
      </p>
    );
  } else if (modal.kind === "quarantined") {
    title = "⛔ Do Not Pick";
    body = (
      <div className="space-y-2">
        <p className="text-base font-semibold text-red-300">{modal.message}</p>
        <p className="text-sm text-gray-400">
          This bag has not been cleared by QC. Set it aside and pick a different bag.
        </p>
      </div>
    );
  } else if (modal.kind === "not_reserved") {
    title = "⛔ Wrong Bag";
    body = (
      <div className="space-y-2">
        <p className="text-base font-semibold text-red-300">{modal.message}</p>
        <p className="text-sm text-gray-400">
          This bag isn’t reserved for this delivery. Pick one of the highlighted bags listed
          under the product.
        </p>
      </div>
    );
  } else if (modal.kind === "already_picked") {
    title = "⛔ Already Picked";
    body = (
      <div className="space-y-2">
        <p className="text-base font-semibold text-red-300">{modal.message}</p>
        <p className="text-sm text-gray-400">
          This bag has no stock left — it’s likely already been dispatched. Set it aside and
          pick a different bag.
        </p>
      </div>
    );
  } else if (modal.kind === "batch_full") {
    title = "Batch complete";
    body = (
      <div className="space-y-2 text-gray-300">
        <p>
          You’ve already scanned all{" "}
          <span className="font-semibold">{String(modal.reserved)}</span> bag
          {modal.reserved !== 1 ? "s" : ""} requested from batch{" "}
          <span className="font-mono">{modal.batch}</span>.
        </p>
        <p className="text-sm text-gray-400">
          Pick the remaining bags from another highlighted batch.
        </p>
      </div>
    );
  } else if (modal.kind === "variance") {
    title = "Extra Bag";
    body = (
      <div className="space-y-2 text-gray-300">
        <p>
          You&apos;ve scanned <span className="font-semibold">{String(modal.actual)}</span> bags
          but only <span className="font-semibold">{String(modal.expected)}</span> were expected.
        </p>
        <p className="text-sm text-gray-400">
          The extra bag has been recorded. Let your supervisor know if this wasn&apos;t intentional.
        </p>
      </div>
    );
  } else {
    title = modal.title;
    body = <p className="text-sm text-gray-400">{modal.message}</p>;
  }

  return (
    <div className="fixed inset-0 flex items-end justify-center bg-black/70 p-4">
      <div className="w-full max-w-sm rounded-2xl bg-gray-800 p-6">
        <h3 className="mb-3 text-lg font-bold text-scan-error">{title}</h3>
        <div className="mb-5">{body}</div>
        <button
          onClick={onDismiss}
          className="w-full rounded-xl bg-green-600 py-3 font-semibold active:bg-green-700"
        >
          Continue scanning
        </button>
      </div>
    </div>
  );
}
